/**
 * The ONE body-level tooltip (`design/DESIGN-CLAUDE.md` "Patterns → Tooltip").
 *
 * Recipe, verbatim from the design rules:
 *   put `data-tip` (UPPERCASE mono copy) on the anchor; the app mounts one
 *   body-level fixed tooltip with a DELEGATED hover listener — it survives
 *   `overflow:hidden` ancestors, which the CSS `::after` approach does not.
 *   `--gt-card` + 1px `--gt-border` + `--gt-shadow-toast`, mono 9px
 *   `--gt-text-3`, `pointer-events:none`, below the anchor, edge-aware.
 *   `data-tip-pos="r"` right-aligns.
 *
 * Contract for the screen lanes:
 *   - Mount exactly once (App.tsx already does). Never render a second one.
 *   - To give any element a tooltip: `data-tip="COPY IN CAPS"` and optionally
 *     `data-tip-pos="r"`. Nothing else — no imports, no props, no wrapper.
 *   - An empty/absent `data-tip` shows nothing.
 *   - Native `title=` stays reserved for inputs/iframes and drag tiles.
 *
 * TOUCH SHOWS NOTHING, and the gate is `pointerType` rather than the absence
 * of hover events. A tap *does* synthesize a `mouseover`, and it arrives AFTER
 * `pointerdown` — so a `mouseover`-driven tip fired second and outlived the
 * clear that was meant to catch it, leaving CLOSE / the call cells stuck under
 * a tooltip until the next tap elsewhere. Listening on `pointerover` instead
 * lets the anchor tell us which device is asking; anything that is not a mouse
 * gets no tip. Pen is lumped in with touch: a pen tap has the same problem and
 * cannot be relied on to fire a leave event afterwards.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';

interface TipState {
  txt: string;
  /** anchor box in viewport coordinates */
  x: number;
  y: number;
  w: number;
  h: number;
  right: boolean;
}

/** gap below the anchor — matches Quarter Icon's own `margin-top:9px` */
const GAP = 9;
/** keep this far from every viewport edge */
const EDGE = 8;

export interface TooltipProps {
  /**
   * Stacking level. Mobile's ladder puts the tip at 110 (above sheets 80/85,
   * scanlines 90 and toasts 100, below the drag ghost 120); the desktop
   * prototype's `#gt-tip` is at **200** (INVENTORY-DESKTOP §4 row 10) because
   * its own ghost is at 9999. Exactly one Tooltip is mounted at a time — the
   * mobile tree mounts `<Tooltip/>`, the desktop tree `<Tooltip zIndex={200}/>`.
   */
  zIndex?: number | undefined;
}

export function Tooltip({ zIndex }: TooltipProps = {}) {
  const [tip, setTip] = useState<TipState | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const over = (ev: PointerEvent) => {
      // An empty `pointerType` means the environment did not tell us (jsdom,
      // a synthetic dispatch); treat that as a mouse so tests and any
      // non-pointer-event fallback keep the desktop behaviour.
      if (ev.pointerType && ev.pointerType !== 'mouse') {
        setTip((prev) => (prev ? null : prev));
        return;
      }
      const target = ev.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest('[data-tip]');
      if (!anchor) {
        setTip((prev) => (prev ? null : prev));
        return;
      }
      const txt = anchor.getAttribute('data-tip') ?? '';
      if (!txt) {
        setTip((prev) => (prev ? null : prev));
        return;
      }
      const r = anchor.getBoundingClientRect();
      setTip({ txt, x: r.left, y: r.top, w: r.width, h: r.height, right: anchor.getAttribute('data-tip-pos') === 'r' });
    };
    const clear = () => setTip((prev) => (prev ? null : prev));

    document.addEventListener('pointerover', over, true);
    document.addEventListener('mouseleave', clear, true);
    document.addEventListener('pointerdown', clear, true);
    window.addEventListener('scroll', clear, true);
    window.addEventListener('resize', clear);
    return () => {
      document.removeEventListener('pointerover', over, true);
      document.removeEventListener('mouseleave', clear, true);
      document.removeEventListener('pointerdown', clear, true);
      window.removeEventListener('scroll', clear, true);
      window.removeEventListener('resize', clear);
    };
  }, []);

  // Position imperatively before paint: the clamp needs the tooltip's own
  // measured box, and a second React pass would flash it at the wrong spot.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !tip) return;
    const tw = el.offsetWidth;
    const th = el.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left = tip.right ? tip.x + tip.w - tw : tip.x;
    left = Math.min(Math.max(left, EDGE), Math.max(EDGE, vw - tw - EDGE));

    let top = tip.y + tip.h + GAP;
    if (top + th + EDGE > vh) {
      const above = tip.y - th - GAP;
      top = above >= EDGE ? above : Math.max(EDGE, vh - th - EDGE);
    }
    el.style.transform = 'translate(' + Math.round(left) + 'px,' + Math.round(top) + 'px)';
    el.style.visibility = 'visible';
  }, [tip]);

  if (!tip) return null;
  return (
    <div
      ref={ref}
      data-qd-tooltip
      style={{
        position: 'fixed',
        left: 0,
        top: 0,
        visibility: 'hidden',
        background: 'var(--gt-card)',
        border: '1px solid var(--gt-border)',
        boxShadow: 'var(--gt-shadow-toast)',
        padding: '7px 9px 6px',
        fontFamily: 'var(--gt-font-mono)',
        fontSize: '9px',
        letterSpacing: '.08em',
        color: 'var(--gt-text-3)',
        whiteSpace: 'nowrap',
        // above sheets (80/85), the scanline overlay (90) and toasts (100);
        // below the drag ghost (120), which owns the pointer while dragging.
        zIndex: zIndex ?? 110,
        pointerEvents: 'none',
      }}
    >
      {tip.txt}
    </div>
  );
}
